import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { cn } from '@/app/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/app/components/ui/sheet'
import { Button } from './button'
import { Trash2, Bell, CheckCircle2 } from 'lucide-react'
import { ConfirmationModal } from './ConfirmationModal'
import { notificationService, type UserNotification } from '@/app/services/notification.service'
import { NotificationStack, type NotificationData, type NotificationType } from './NotificationItem'

// ─── Tipos de Datos e Interfaces ─────────────────────────────────────────
interface NotificationButtonProps {
  mode: 'popover' | 'drawer'
  className?: string
}

const mapBackendToUI = (backendNotification: UserNotification): NotificationData => {
  return {
    id: backendNotification.id,
    type: (backendNotification.module || 'system') as NotificationType,
    title: backendNotification.title,
    description: backendNotification.description || '',
    isRead: backendNotification.viewed_at !== null,
    createdAt: new Date(backendNotification.created_at).toLocaleDateString(),
    actionText: backendNotification.is_redirectable ? 'Ver detalles' : undefined,
    actionUrl: backendNotification.details?.redirect_path,
  }
}

// ─── Componente Principal ────────────────────────────────────────────────
export function NotificationButton({ mode, className }: NotificationButtonProps) {
  const navigate = useNavigate()

  // ─── Estados  ───
  const [open, setOpen] = useState(false)
  const [isRinging, setIsRinging] = useState(false)
  const [notifications, setNotifications] = useState<NotificationData[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean
    preset: 'delete' | 'toggleStatus' | 'cancel' | 'reject'
    itemName: string
    onConfirm: () => void
  }>({
    open: false,
    preset: 'delete',
    itemName: '',
    onConfirm: () => { },
  })

  const hasUnread = unreadCount > 0

  // ─── Efecto: Cargar Datos y Polling ───
  const loadNotifications = async (page = 1) => {
    setIsLoading(true)
    try {
      const response = await notificationService.getNotifications({ page })
      const mapped = response.data.map(mapBackendToUI)
      if (page === 1) {
        setNotifications(mapped)
      } else {
        setNotifications(prev => [...prev, ...mapped])
      }
      setHasMore(response.current_page < response.last_page)
      setCurrentPage(page)

      const count = await notificationService.getUnreadCount()
      setUnreadCount(count)
    } catch (error) {
      console.error('Error loading notifications:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications(1)

    // 5 seg
    const interval = setInterval(() => {
      if (!open) loadNotifications(1)
    }, 5000)

    return () => clearInterval(interval)
  }, [open])

  // Animar la campana cada 30 segundos si hay notificaciones sin leer
  useEffect(() => {
    if (!hasUnread) {
      setIsRinging(false)
      return
    }

    setIsRinging(true)
    const timeout = setTimeout(() => setIsRinging(false), 1000)

    const interval = setInterval(() => {
      setIsRinging(true)
      setTimeout(() => setIsRinging(false), 1000)
    }, 30000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [hasUnread])

  // ─── Funciones de Interfaz ───
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
  }

  const handleActionClick = (notification: NotificationData) => {
    if (notification.actionUrl) {
      setOpen(false) // Cierra el drawer al navegar
      navigate(notification.actionUrl)
    }
  }

  const handleToggleRead = async (id: string | number) => {
    const isCurrentlyRead = notifications.find(n => n.id === id)?.isRead
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: !n.isRead } : n)
    )
    setUnreadCount(prev => isCurrentlyRead ? prev + 1 : Math.max(0, prev - 1))

    try {
      if (isCurrentlyRead) {
        await notificationService.markAsUnviewed(id)
      } else {
        await notificationService.markAsViewed(id)
      }
    } catch (error) {
      loadNotifications(1)
    }
  }

  const handleDelete = async (id: string | number) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
    try {
      await notificationService.deleteNotification(id)
    } catch (error) {
      loadNotifications(1)
    }
  }

  const handleDeleteGroup = async (type: NotificationType) => {
    setNotifications(prev => prev.filter(n => n.type !== type))
    try {
      await notificationService.deleteGroup(type)
    } catch (error) {
      loadNotifications(1)
    }
  }

  const handleClearAll = async () => {
    setNotifications([])
    setUnreadCount(0)
    try {
      await notificationService.deleteAll()
    } catch (error) {
      loadNotifications(1)
    } finally {
      setConfirmModal(prev => ({ ...prev, open: false }))
    }
  }

  const openClearAllModal = () => {
    setConfirmModal({
      open: true,
      preset: 'delete',
      itemName: 'todas las notificaciones',
      onConfirm: handleClearAll,
    })
  }

  const handleLoadMore = () => {
    loadNotifications(currentPage + 1)
  }

  // Agrupación por tipo de notificación
  const groupedNotifications = Object.values(
    notifications.reduce((acc, notification) => {
      if (!acc[notification.type]) acc[notification.type] = []
      acc[notification.type].push(notification)
      return acc
    }, {} as Record<string, NotificationData[]>)
  )

  // ─── Renderizado (La Vista HTML JSX) ──────────────────────────────────────

  // 1. Boton Campana
  const TriggerButton = (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "relative rounded-full h-11 w-11 hover:bg-slate-100/80 hover:shadow-sm border border-transparent transition-all",
        open && "bg-slate-100 shadow-sm border-slate-200",
        className
      )}
    >
      <div className={cn("flex h-7 w-7 items-center justify-center transition-transform duration-200 origin-top", isRinging && "animate-ring")}>
        <Bell className="h-[26px] w-[26px]" />
      </div>
      {hasUnread && (
        <span className={cn(
          "absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white shadow-sm transition-transform duration-300",
          open ? "scale-0" : "scale-100"
        )}>
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  )

  // 2. Contenido del panel
  const PanelContent = (
    <div className="flex h-full flex-col bg-card text-card-foreground relative overflow-hidden">
      <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80" aria-hidden="true">
        <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#9089fc] to-[#4ade80] opacity-10 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" />
      </div>

      <div className="flex items-center justify-between border-b border-border bg-table-header-bg px-6 py-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-card-foreground">Notificaciones</h2>
          {hasUnread && (
            <span className="rounded-full border border-orange-500/20 bg-orange-500/10 px-2 py-0.5 text-xs font-bold text-orange-500">
              {unreadCount} nuevas
            </span>
          )}
        </div>
        {notifications.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={openClearAllModal}
            className="z-50 h-8 rounded-full px-3 text-xs font-semibold text-card-description transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Limpiar todo
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col px-6 py-4">
        {groupedNotifications.length > 0 ? (
          <>
            {groupedNotifications.map((group) => (
              <NotificationStack
                key={group[0].type}
                items={group}
                type={group[0].type}
                onActionClick={handleActionClick}
                onToggleRead={handleToggleRead}
                onDelete={handleDelete}
                onDeleteGroup={handleDeleteGroup}
              />
            ))}

            {hasMore && (
              <div className="mt-4 mb-2 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="rounded-full border-border bg-card px-4 text-xs font-semibold text-card-foreground shadow-sm hover:bg-table-row-hover"
                >
                  {isLoading ? 'Cargando...' : 'Cargar más antiguas'}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 text-center px-4 opacity-80">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-primary/10 shadow-sm">
            <Bell className="h-8 w-8 text-primary" />
          </div>

          <h3 className="text-sm font-bold text-card-foreground">
            Todo al día
          </h3>

          <p className="mt-1 max-w-[200px] text-xs leading-relaxed text-card-description">
            No tienes notificaciones pendientes por el momento.
          </p>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmModal.open}
        preset={confirmModal.preset}
        itemName={confirmModal.itemName}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  )

  if (mode === 'drawer') {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          {TriggerButton}
        </SheetTrigger>
        <SheetContent className="w-full border-l-border p-0 shadow-2xl sm:max-w-md">
          <SheetHeader className="sr-only">
            <SheetTitle>Centro de Notificaciones</SheetTitle>
          </SheetHeader>
          {PanelContent}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {TriggerButton}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] overflow-hidden rounded-2xl border-border p-0 shadow-2xl"
        sideOffset={8}
      >
        <div className="h-[500px]">
          {PanelContent}
        </div>
      </PopoverContent>
    </Popover>
  )
}
